// useOTPVerification Hook
// Location: src/hooks/useOTPVerification.ts
// Purpose: Verify OTP codes and manage device trust

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDeviceFingerprint } from './useDeviceFingerprint';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export const useOTPVerification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { deviceInfo, isReady: isDeviceReady } = useDeviceFingerprint();

  const email = searchParams.get('email') || '';
  const userId = searchParams.get('userId') || '';
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [canResend, setCanResend] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Validate required params on mount
  useEffect(() => {
    if (!email || !userId) {
      toast.error(t('auth.errors.invalidOTPLink') || 'Invalid verification link');
      navigate('/login');
    }
  }, [email, userId, navigate, t]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCooldown === 0) {
      setCanResend(true);
    }
  }, [resendCooldown]);

  const handleOtpChange = (value: string) => {
    // Only allow digits
    const cleaned = value.replace(/\D/g, '');
    // Limit to 6 digits
    setOtpCode(cleaned.substring(0, 6));
  };

  const verifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error(t('auth.errors.otpInvalid') || 'Please enter a valid 6-digit code');
      return;
    }

    if (!isDeviceReady || !deviceInfo) {
      toast.error(t('auth.errors.deviceFingerprintNotReady') || 'Device identification not ready');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-verify-device-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            user_id: userId,
            otp_code: otpCode,
            device_fingerprint: deviceInfo.fingerprint,
            device_info: {
              name: deviceInfo.name,
              type: deviceInfo.type,
              browser: deviceInfo.browser,
              os: deviceInfo.os,
              user_agent: deviceInfo.user_agent
            },
            remember_me: true // Default to trusting device
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        // Update attempts remaining if provided
        if (result.attempts_remaining !== undefined) {
          setAttemptsRemaining(result.attempts_remaining);
        }
        
        throw new Error(result.error || 'OTP verification failed');
      }

      // Success — exchange the recovery token for a real session
      const { token_hash, email, type } = result.auth;

      if (!token_hash || !email) {
        throw new Error('Auth token missing from server response');
      }

      const { error: sessionError } = await supabase.auth.verifyOtp({
        type: type || 'recovery',
        token_hash,
        // email,
      });

      if (sessionError) {
        throw sessionError;
      }

      toast.success(t('auth.otp.verificationSuccess'));

      const decodedRedirect = decodeURIComponent(redirectUrl);
      navigate(decodedRedirect);

    } catch (error: any) {
      // console.error('OTP verification error:', error);
      
      const errorMessage = error.message?.toLowerCase() || '';
      
      if (errorMessage.includes('incorrect')) {
        toast.error(t('auth.errors.otpIncorrect') || 'Incorrect verification code');
      } else if (errorMessage.includes('expired')) {
        toast.error(t('auth.errors.otpExpired') || 'Verification code expired. Please request a new one.');
      } else if (errorMessage.includes('max')) {
        toast.error(t('auth.errors.otpMaxAttempts') || 'Maximum attempts exceeded. Please request a new code.');
      } else {
        toast.error(error.message || t('auth.errors.otpVerificationFailed') || 'Verification failed');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const resendOTP = async () => {
    if (!canResend || isResending) return;

    if (!isDeviceReady || !deviceInfo) {
      toast.error(t('auth.errors.deviceFingerprintNotReady') || 'Device identification not ready');
      return;
    }

    setIsResending(true);
    setCanResend(false);

    try {
      // Request new OTP by calling sign-in again
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-sign-in`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            email,
            // Note: We don't have password here, so this should be handled by a separate endpoint
            // For now, we'll call the OTP resend endpoint (to be created)
            device_fingerprint: deviceInfo.fingerprint,
            device_info: {
              name: deviceInfo.name,
              type: deviceInfo.type,
              browser: deviceInfo.browser,
              os: deviceInfo.os,
              user_agent: deviceInfo.user_agent
            },
            resend_otp: true,
            user_id: userId
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to resend code');
      }

      toast.success(t('auth.login.otpSent') || 'New verification code sent');
      
      // Reset OTP input
      setOtpCode('');
      setAttemptsRemaining(null);
      
      // Start 60 second cooldown
      setResendCooldown(60);

    } catch (error: any) {
      // console.error('Resend OTP error:', error);
      toast.error(error.message || t('auth.errors.resendFailed') || 'Failed to resend code');
      setCanResend(true); // Allow retry on error
    } finally {
      setIsResending(false);
    }
  };

  return {
    email,
    userId,
    otpCode,
    isVerifying,
    attemptsRemaining,
    canResend,
    resendCooldown,
    isResending,
    isDeviceReady,
    deviceInfo,
    handleOtpChange,
    verifyOTP,
    resendOTP
  };
};